/**
 * InsightsErrorBoundary — Section-level error boundary for VS Insights
 *
 * Catches React render errors in Insights components (especially Chart.js)
 * without redirecting away from the page. Shows a retry-friendly fallback
 * UI within the dashboard context.
 *
 * This complements the app-wide ErrorBoundary (src/components/ui/ErrorBoundary.tsx)
 * which handles catastrophic errors with a full-page redirect.
 */
import { Component, ErrorInfo, ReactNode } from 'react';
import { FiAlertCircle, FiRefreshCw } from 'react-icons/fi';

interface InsightsErrorBoundaryProps {
  children: ReactNode;
  /** Optional section name for logging context (e.g., "Financial Health") */
  section?: string;
}

interface InsightsErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class InsightsErrorBoundary extends Component<InsightsErrorBoundaryProps, InsightsErrorBoundaryState> {
  constructor(props: InsightsErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): Partial<InsightsErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const section = this.props.section ?? 'VS Insights';
    console.error(`🚨 [InsightsErrorBoundary] Error in ${section}:`, error);
    console.error('📍 Component stack:', errorInfo.componentStack);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const section = this.props.section ?? 'Insights';

      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 sm:p-10 flex flex-col items-center justify-center text-center min-h-[200px]">
          <div className="bg-red-100 p-3 rounded-full mb-4">
            <FiAlertCircle className="text-red-500" size={24} />
          </div>
          <h4 className="text-lg font-semibold text-red-700 mb-2">
            Something went wrong loading {section}
          </h4>
          <p className="text-sm text-red-500 max-w-md mb-6">
            An unexpected error occurred while rendering this section.
            This has been logged for review.
          </p>
          {import.meta.env.DEV && this.state.error && (
            <pre className="text-xs text-red-400 bg-red-100 rounded p-3 mb-4 max-w-lg overflow-auto text-left">
              {this.state.error.message}
            </pre>
          )}
          <button
            onClick={this.handleRetry}
            className="inline-flex items-center gap-2 bg-red-600 text-white font-medium text-sm px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            <FiRefreshCw size={14} />
            Retry
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default InsightsErrorBoundary;
